import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import qs from 'qs';
import { baseURL } from '~/utils/constants/env';
import { LanguageEnum, StorageEnum } from '~/utils/enum';
import { TIME_OUT } from '~/utils/constants/common';
import i18n from '~/utils/i18n';

let onUnauthorized: (() => void) | null = null;
let onRefreshToken: (() => { access_token: string } | Promise<{ access_token: string }>) | null = null;
let isRefreshing = false;
let failedQueue: any[] = [];

const publicPaths = ['/auth/login', '/auth/register/request-otp', '/auth/register/verify-otp'];

export const setOnUnauthorized = (callback: () => void) => {
  onUnauthorized = callback;
};

export const setOnRefreshToken = (callback: () => { access_token: string } | Promise<{ access_token: string }>) => {
  onRefreshToken = callback;
};

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const PARAMS_SERIALIZE_OPTIONS: qs.IStringifyOptions = {
  arrayFormat: 'repeat',
  skipNulls: true,
  format: 'RFC3986',
};

export function serializeAxiosQueryParams(params: Record<string, unknown>): string {
  return qs.stringify(params, PARAMS_SERIALIZE_OPTIONS);
}

const axiosClient = axios.create({
  baseURL: baseURL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  timeout: TIME_OUT,
  paramsSerializer: serializeAxiosQueryParams,
});

axiosClient.interceptors.request.use(
  function (config: InternalAxiosRequestConfig) {
    const token = localStorage.getItem(StorageEnum.ACCESS_TOKEN);
    config.headers.Authorization = token ? `Bearer ${token}` : '';
    config.headers['Accept-Language'] = i18n.language || LanguageEnum.VI;
    return config;
  },

  function (error: AxiosError | Error) {
    return Promise.reject(error);
  }
);

axiosClient.interceptors.response.use(
  function (response: AxiosResponse) {
    return response;
  },

  function (error: AxiosError | Error) {
    return Promise.reject(error);
  }
);

axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest: any = error.config;
    if (publicPaths.some((path) => originalRequest.url?.includes(path))) return Promise.reject(error);

    if (originalRequest.url?.includes('/api/auth/refresh') && error.response?.status) {
      if (onUnauthorized) {
        onUnauthorized();
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return axiosClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        if (!onRefreshToken) {
          throw new Error('');
        }
        const res = await onRefreshToken();
        processQueue(null, res.access_token);

        originalRequest.headers['Authorization'] = 'Bearer ' + res.access_token;
        return axiosClient(originalRequest);
      } catch (err) {
        processQueue(err, null);
        if (onUnauthorized) {
          onUnauthorized();
        }
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export default axiosClient;
